import * as THREE from 'three';
import { EG, enemyPalette } from '../entities/enemy-parts';
import { scene } from './scene';
import { softShadowMat } from './contact-shadow';
/* ============================================================
   Field —— 小怪的實例化渲染層（移植自 `enemy_sample_ani.html` §8）
   ============================================================

   權威規格 `05-ENEMY.md` §4。這一層存在的理由只有一句：

   > **instanced 的 draw call 是按「型別數」算的，不是按敵人數。**

   一個型別 ≈ 6.3 個 primitive，每個 primitive 一顆 `InstancedMesh` ——
   所以場上 420 隻同型是 6.3 個 draw call，420 隻分 28 型是 176 個。
   敵人數量免費，型別數量不是。**波次設計因此就是 draw call 預算**。

   與舊的 `render/enemy-instances.ts` 的差別：舊版是「保留每隻的 Group 階層、
   算完 matrixWorld 再複製進 InstancedMesh」，那是為了沿用既有的動畫程式碼。
   新版的動畫本來就寫成「part 表 + fn(P, o)」，沒有階層可言（規格 §6.1 的
   `pivotTop` 就是為了補這件事），所以直接算矩陣，不需要影子階層。

   三個實作要點：

   1. **小怪一律 `castShadow = false`**。真陰影會讓每顆 InstancedMesh 多跑一次
      depth pass，draw call 直接翻倍，換到的是 20px 下看不見的東西。
      改用一顆壓扁的圓盤當接地陰影 —— 而且**全場共用一顆**，所以是 1 個 draw call
   2. **受擊閃白走 `instanceColor`**。材質是型別共用的，改材質會讓整型別一起閃
   3. 每個型別的容量獨立成長：`ensure()` 會在超過時重建（重建成本高，
      所以一開就給該型別的合理上限） */

var _obj = new THREE.Object3D();      // 部件的暫存變換
var _root = new THREE.Object3D();     // 個體的暫存變換（位置 + 朝向 + pose）
var _col = new THREE.Color();
var GEO = { box: EG.box, cyl: EG.cyl, bev: EG.bev, cone: EG.cone, oct: EG.oct, tor: EG.tor };
var K_BEV = 1 / Math.cos(Math.PI / 8);

/** 接地陰影：壓扁的圓柱（原型用 `G.cyl`，10 邊；不是 `CircleGeometry`），
    **全場一顆 InstancedMesh**。
    對 WING 家族這是必要的不是裝飾 —— 浮空的辨識靠「影子離身體很遠」，
    接近正俯視時高度差會被壓掉，那時候影子是唯一還在的線索（規格 §4.2）。 */
/* 2026-08-04 質感這一輪：實心圓柱 → **柔光平面**。
   壓扁的圓柱畫出來是一片**硬邊的純黑餅**，在暗地面上讀成「破圖」而不是
   影子；換成徑向漸層之後才讀得出是接觸面的環境遮蔽。
   ⚠️ 平面做成 2.4 見方而不是 1：柔光圓的可見半徑只有貼圖的三分之一，
   維持 1 的話影子會縮成原本的三分之一（而呼叫端的 scale 不必動）。 */
var SDW_GEO = new THREE.PlaneGeometry(2.4, 2.4); SDW_GEO.rotateX(-Math.PI / 2);
var SDW_MAT = softShadowMat(0.5);

/** 精英的冠環：**28 種精英共用同一顆**（規格 §2.4）。
    28 套獨特造型等於要玩家學 56 種而不是 28+1 種；共用冠環讓「精英」
    變成一個可以被學習的符號，而且全場只要 1 個 draw call。

    原型的 `eliteKit()` 除了冠環還有頂珠、天線與兩片肩甲 ——
    **使用者 2026-08-03 拍板不移植那四個**（見 `enemy-parts.ts` 檔尾）。 */
var CRN_GEO = new THREE.TorusGeometry(0.40, 0.055, 6, 14);
var CRN_MAT = new THREE.MeshBasicMaterial({ color: 0xffd98a, transparent: true, opacity: 0.95 });

/** 精英的材質映射：**armor 亮一階**（規格 §2.4）。
    只換材質不換形狀 —— 精英要讀作「一樣的東西，但難打」，
    換了形狀就變成第 29 種敵人。 */
var EL_MAT = { armor: 'armorE', glow: 'glowE' };

/** 一個型別的精英版：同一張 part 表換亮一階的材質 ＋ `ex[]` 額外部件。
    快取在型別物件上（`_e`）—— 每幀重建會讓 `ensure()` 一直認不得它。 */
export function eliteType(T) {
  if (T._e) return T._e;
  var parts: any[] = [];
  for (var i = 0; i < T.parts.length; i++) {
    var q = T.parts[i];
    parts.push({ g: q.g, m: EL_MAT[q.m] || q.m, s: q.s, p: q.p, r: q.r, fn: q.fn });
  }
  if (T.ex) parts = parts.concat(T.ex);
  T._e = { id: T.id + ':e', tint: T.tint, w: T.w, h: T.h, fly: T.fly, parts: parts, cap: 12 };
  return T._e;
}

export function createField(cap?) {
  cap = cap || 512;
  var kinds = {};                       // typeId → { T, parts:[InstancedMesh], n }
  var sdw = new THREE.InstancedMesh(SDW_GEO, SDW_MAT, cap);
  sdw.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  sdw.frustumCulled = false; sdw.renderOrder = 3;
  /* 圓柱的軸本來就是 Y，**不需要任何旋轉**（上一版用 `CircleGeometry` 才要轉
     −90°，而且父物件與實例矩陣各轉一次的話圓盤會立起來變成一片牆）。 */
  scene.add(sdw);
  var crn = new THREE.InstancedMesh(CRN_GEO, CRN_MAT, 24);
  crn.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  crn.frustumCulled = false; crn.count = 0;
  scene.add(crn);
  var crnN = 0;

  function ensure(T, want) {
    var k = kinds[T.id];
    if (k && k.cap >= want) return k;
    if (k) for (var d = 0; d < k.parts.length; d++) { scene.remove(k.parts[d]); k.parts[d].dispose(); }
    var pal = enemyPalette(T.tint);
    var list = [];
    for (var i = 0; i < T.parts.length; i++) {
      var q = T.parts[i];
      var im = new THREE.InstancedMesh(GEO[q.g], pal[q.m], want);
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      /* `instanceColor` 一開始就配置：之後才加的話 three 不會補上 attribute，
         而受擊那一幀才發現就來不及了。全部填 1 = 不改色。 */
      im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(want * 3).fill(1), 3);
      im.instanceColor.setUsage(THREE.DynamicDrawUsage);
      im.castShadow = false; im.receiveShadow = false;   // 見檔頭第 1 點
      im.frustumCulled = false;                          // 位置每幀重寫，包圍球沒有意義
      scene.add(im); list.push(im);
    }
    kinds[T.id] = k = { T: T, parts: list, cap: want, n: 0 };
    return k;
  }

  /** 一幀的開始：所有型別的計數歸零。 */
  function begin() {
    for (var id in kinds) kinds[id].n = 0;
    sdwN = 0; crnN = 0;
  }
  var sdwN = 0;

  /** 放一隻進這一幀的批次。`o` 是規格 §6.2 的動畫通道物件（見 `enemy-parts.ts`）。 */
  function push(T, o) {
    var k = ensure(T, T.cap || 220);
    if (k.n >= k.cap) return;
    var idx = k.n++;
    /* 受擊的瞬間整隻微放大 1.10 倍（原型 `stepActor`：`sc = base*(1+0.10*hit)`）。
       白閃只改顏色，20px 下顏色的變化比體積小 —— 兩個一起做才讀得到「打中了」。 */
    var sc = (o.sc === undefined ? 1 : o.sc) * (1 + 0.10 * (o.hit || 0));

    /* 個體的變換：位置 → 朝向 → pose（浮沉／前後傾／側傾／沿朝向位移）。
       pose 寫在 root 上而不是每個部件上，**沒有 fn 的部件才合併得掉**
       （精英 rig 走同一份 part 表，規格 §6.2）。 */
    _root.position.set(o.x + Math.sin(o.yaw) * (o.rz || 0), o.ry || 0, o.z + Math.cos(o.yaw) * (o.rz || 0));
    _root.rotation.set(o.rp || 0, o.yaw, o.rr || 0);
    _root.scale.setScalar(sc);
    _root.updateMatrix();

    for (var i = 0; i < T.parts.length; i++) {
      var q = T.parts[i];
      var P: any = {
        sx: q.s[0], sy: q.s[1], sz: q.s[2],
        x: q.p[0], y: q.p[1], z: q.p[2],
        rx: q.r ? q.r[0] : 0, ry: q.r ? q.r[1] : 0, rz: q.r ? q.r[2] : 0
      };
      /* bev 的 x/z 量到角不是量到面 —— 與機體同一份規格。
         **補正要在 `fn` 之前**（原型 `fillP()` 就是這個順序）：
         `fn` 裡有「讀 P.sx 再推算位移」的寫法，補在後面的話那些式子讀到的
         是未補正的值，差 8.2%。純乘法的 fn 兩種順序結果相同，但不是全部都是純乘法。 */
      if (q.g === 'bev') { P.sx *= K_BEV; P.sz *= K_BEV; }
      if (q.fn) q.fn(P, o);
      _obj.position.set(P.x, P.y, P.z);
      _obj.rotation.set(P.rx, P.ry, P.rz);
      _obj.scale.set(P.sx, P.sy, P.sz);
      _obj.updateMatrix();
      _obj.matrix.premultiply(_root.matrix);
      k.parts[i].setMatrixAt(idx, _obj.matrix);
      /* 受擊閃白：> 1 是刻意的 —— 配合 ACES tone mapping 打出過曝白閃。
         材質共用，所以只能走 instanceColor（規格 §6.5）。 */
      var f = 1 + 5.5 * (o.hit || 0);
      _col.setRGB(f, f, f);
      k.parts[i].setColorAt(idx, _col);
    }

    /* 接地陰影（原型 `Field.sync` 尾段）：
         w = T.w × (浮空 ? 0.62 : 1) × (1 − 0.5·die)
       局部矩陣 `pos(0, 0.02, 0) · scale(w, 0.01, w×0.92)`，再乘上**躺平版**的
       個體矩陣 —— 所以影子跟著朝向轉（0.92 的橢圓才讀得出前後）、也跟著
       `o.rz`（沿朝向的位移）走，但不吃浮沉與傾角。

       **原型的 `w` 裡還乘了一次 `o.sc`，這裡刻意不抄**：個體矩陣本身已經帶了
       `o.sc`，兩邊都乘等於平方。原型的 `sc` 恆等於 1（沒有體積倍率）所以看不出來，
       我們的 `sc ≈ 2`（`BODY_K`）—— 照抄的話影子會是身體的兩倍寬。 */
    if (sdwN < cap) {
      var w = (T.w || 1) * (T.fly ? 0.62 : 1) * (1 - 0.5 * (o.die || 0));
      _root.position.set(o.x + Math.sin(o.yaw) * (o.rz || 0), 0, o.z + Math.cos(o.yaw) * (o.rz || 0));
      _root.rotation.set(0, o.yaw, 0);
      _root.scale.setScalar(sc);
      _root.updateMatrix();
      _obj.position.set(0, 0.06, 0);   // 貼花 0.02、雲影 0.04 之上（見 contact-shadow.ts）
      _obj.rotation.set(0, 0, 0);
      _obj.scale.set(w, 0.01, w * 0.92);
      _obj.updateMatrix();
      _obj.matrix.premultiply(_root.matrix);
      sdw.setMatrixAt(sdwN++, _obj.matrix);
    }

    /* 冠環浮在頭頂上方，**平躺**（torus 預設軸是 Z，躺平要 rx = -π/2；
       規格 §6.4 那個坑）。慢速自轉讓它在靜止的雜魚堆裡跳出來。

       `crownH` 與 `T.w` 都是 **scale 1 的尺寸**，這裡乘 `sc` 才是世界大小。
       兩邊都先乘過的話會放大兩次（記過一次，見 `data/enemies.ts` 的 `h`/`w`）。 */
    if (o.crown && crnN < 24) {
      var cr = (o.crownH || 1.4) * sc;
      _obj.position.set(o.x, cr + 0.26 + Math.sin(o.t * 2.2) * 0.05, o.z);
      _obj.rotation.set(-Math.PI / 2, 0, o.t * 1.1);
      _obj.scale.setScalar((T.w || 1) * 1.35 * sc);
      _obj.updateMatrix();
      crn.setMatrixAt(crnN++, _obj.matrix);
    }
  }

  /** 一幀的結尾：把用不到的實例縮到 0（不是刪，刪要重建 buffer）並送出更新。 */
  function end() {
    for (var id in kinds) {
      var k = kinds[id];
      for (var i = 0; i < k.parts.length; i++) {
        var im = k.parts[i];
        im.count = k.n;
        im.instanceMatrix.needsUpdate = true;
        if (im.instanceColor) im.instanceColor.needsUpdate = true;
      }
    }
    sdw.count = sdwN;
    sdw.instanceMatrix.needsUpdate = true;
    crn.count = crnN;
    crn.instanceMatrix.needsUpdate = true;
  }

  /** 診斷。**`live` 才是 draw call，`meshes` 不是。**

      2026-08-03 實測（three r128）：`count === 0` 的 `InstancedMesh`
      **不會產生 draw call** —— `WebGLBufferRenderer.renderInstances()`
      在 instanceCount 為 0 時直接 return。所以「型別註冊了就一直付錢」
      這個直覺是錯的：28 型全開時 `meshes` 是 177 而 `calls` 只有 74
      （場上只有幾隻），退場機制因此是白做的，做了反而多一次重配 buffer。

      規格 §4 的「28 型 420 隻 = 203 個 draw call」仍然成立 ——
      它講的是**同時活著**的型別數。名冊每 2–3 分鐘換一批（`data/enemies.ts`）
      就是為了把那個數字壓在 8 型以內。 */
  function stats() {
    var meshes = 2, types = 0, live = 2, alive = 0;   // 2 = 共用的接地陰影 + 冠環
    for (var id in kinds) {
      var k = kinds[id];
      meshes += k.parts.length; types++;
      if (k.n > 0) { live += k.parts.length; alive++; }
    }
    return { types: types, meshes: meshes, live: live, alive: alive };
  }

  function clear() {
    for (var id in kinds) {
      var k = kinds[id];
      for (var i = 0; i < k.parts.length; i++) { scene.remove(k.parts[i]); k.parts[i].dispose(); }
    }
    kinds = {};
  }

  return { begin: begin, push: push, end: end, stats: stats, clear: clear, sdw: sdw };
}

/* ---------- 遊戲本體的入口 ---------- */

var FIELD: any = null;
/** 全場唯一的一份。延遲建立 —— 模組載入時 `scene` 還沒準備好。 */
export function enemyField() { return FIELD || (FIELD = createField()); }

/** 每個**畫面幀**呼叫一次，而且一定要在 `interpolate()` 之後：
    這一幀要畫的是內插後的位置，不是模擬步的位置。

    這裡是「模擬狀態 → 動畫通道 `o`」的唯一翻譯處（規格 §6.2）。
    造型程式只看得到 `o`，看不到敵人物件 —— 所以家族檔可以在圖鑑、
    檢視台、遊戲本體三個地方共用同一份，不用知道自己在哪裡跑。 */
export function syncEnemyField(list, t) {
  var F = enemyField();
  F.begin();
  for (var i = 0; i < list.length; i++) {
    var e = list[i], T = e.T;
    if (!T.fam) continue;                 // Boss 走真 rig，不在這一層
    var o = e.o;
    o.x = e.root.position.x; o.z = e.root.position.z;
    /* **每一隻的動畫時鐘要錯開**（`e.tOff` 由 `spawn.ts` 從 `phase` 推導）。
       共用同一個 `t` 的話同型的三百隻會同步呼吸、同步自轉。 */
    o.yaw = e.face; o.t = t + e.tOff;
    o.ph = e.ph; o.mv = e.mv; o.tel = e.tel; o.atk = e.atk;
    o.hit = Math.min(1, e.flash * 6);
    o.q = e.q; o.sc = e.root.scale.x;
    /* pose 是**指派**不是累加（家族檔各自寫 `o.rp = ...`），
       所以踉蹌傾倒要在 pose 之後才疊上去，否則會被蓋掉。 */
    o.ry = 0; o.rp = 0; o.rr = 0; o.rz = 0;
    if (T.pose) T.pose(o);
    o.rr += e.root.rotation.z;
    /* 冠環的掛點：**浮空型的「頂」不是 `h`**（那是造型的總高，量到腳下的
       空氣），而是機身高 + 0.62。少了這一行，HORCHER 精英的冠環會浮在
       牠上方兩公尺處，看起來像另一個獨立的東西。 */
    o.crown = !!e.elite; o.crownH = T.crownH || T.h;
    F.push(e.elite ? eliteType(T) : T, o);
  }
  stepCorpses(F, t);
  F.end();
}

/* ---------- 死亡塌陷（純視覺，模擬層不知道它存在） ----------

   規格 §9 開頭：**小怪的死亡是「塌下去 + 縮小」，0.6 秒結束** ——
   那是給一次死二十隻的東西用的（Boss 才有四拍解體）。
   原型 `stepActor` 的死亡分支：`die += dt×1.7`（≈0.59 秒）、
   `sc = base×(1 − 0.88·die)`、`rp = 0.9·die`、`ry = −0.10·die`，
   接地陰影同步縮到一半。

   **屍體不進模擬層**：`killEnemy` 照舊立刻把敵人從 `enemies` 移除，
   這裡只留一份「當下的樣子」的副本繼續演 0.6 秒。
   放進模擬層的話 `enemies.length` 與索引都會變，決定性快照跟著變 ——
   一個純視覺的效果不該動到黃金雜湊。 */

var corpses: any[] = [];
var CORPSE_CAP = 96;

/** `killEnemy` 呼叫。把這一隻的當下狀態複製一份出來演塌陷。 */
export function addCorpse(e) {
  var T = e.T;
  if (!T || !T.fam) return;                     // Boss 不走這條
  if (corpses.length >= CORPSE_CAP) corpses.shift();
  var o = e.o;
  corpses.push({
    T: e.elite ? eliteType(T) : T,
    o: {
      x: o.x, z: o.z, yaw: o.yaw, t: o.t, ph: o.ph, mv: 0,
      tel: o.tel, atk: o.atk, hit: 0, die: 0.001, p2: 0,
      sc: o.sc, base: o.sc, q: o.q, ry: 0, rp: 0, rr: 0, rz: 0
    }
  });
}

/** 每個畫面幀推進一次。`t` 是全域時間，用來算 dt。 */
var lastT = 0;
function stepCorpses(F, t) {
  var dt = Math.min(0.1, Math.max(0, t - lastT));
  lastT = t;
  for (var i = corpses.length - 1; i >= 0; i--) {
    var c = corpses[i], o = c.o;
    o.t = t;
    o.die = Math.min(1, o.die + dt * 1.7);
    if (o.die >= 1) { corpses.splice(i, 1); continue; }
    o.sc = o.base * (1 - 0.88 * o.die);
    /* 原型的死亡分支**在呼叫 `pose()` 之前就 return** —— 塌下去的姿勢只有
       這兩個值，不疊型別自己的 pose（否則走路的擺動會一路演到屍體上）。 */
    o.rp = 0.9 * o.die; o.ry = -0.10 * o.die; o.rr = 0; o.rz = 0;
    F.push(c.T, o);
  }
}

/** 開新的一場要清乾淨 —— 不清的話上一場的屍體會出現在新場地上。 */
export function clearCorpses() { corpses.length = 0; }

